/**
 * Admin support + feedback queries — ticket list, status updates,
 * AI triage re-run, feedback list, and the shared contact-form submit
 * used by both the support form and the feedback form.
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from '../_edge'
import { CONTACT_FORM, TRIAGE_SUPPORT_TICKET } from '../_endpoints'
import { ADMIN_FEEDBACK_LIMIT, ADMIN_SUPPORT_REQUESTS_LIMIT } from '../_constants'

/** Load support requests, newest first (paginated). */
export async function fetchSupportRequests({
  limit = ADMIN_SUPPORT_REQUESTS_LIMIT,
  offset = 0,
} = {}) {
  const { data, error } = await supabase
    .from('support_requests')
    .select(
      'id, name, email, subject, category, message, status, ai_topic, ai_draft_reply, ai_classified_at, created_at'
    )
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return data || []
}

/** Update a support request's status. */
export async function updateSupportStatus(id, status) {
  const { error } = await supabase.from('support_requests').update({ status }).eq('id', id)
  if (error) throw error
}

/** Re-run AI classification + draft reply for a ticket. Calls the
 *  triage-support-ticket edge function, which writes ai_topic,
 *  ai_draft_reply, and ai_classified_at back to the row, then returns the
 *  updated support_requests row. */
export async function reclassifyTicket(ticketId) {
  const data = await invokeEdgeJson(
    TRIAGE_SUPPORT_TICKET,
    { ticket_id: ticketId },
    { errorMessage: 'Re-classification failed' }
  )
  if (!data?.ticket) throw new Error(data?.error || 'Re-classification failed')
  return data.ticket
}

/** Load all message feedback (admin view). */
export async function fetchAllFeedback() {
  const { data, error } = await supabase
    .from('message_feedback')
    .select('id, message_id, user_id, vote, reason, tab, created_at')
    .order('created_at', { ascending: false })
    .limit(ADMIN_FEEDBACK_LIMIT)
  if (error) throw error
  return data || []
}

/** Submit a contact-form payload (feedback or support). Unauthenticated
 *  endpoint — the support/feedback UI is reachable by logged-out users. */
export async function submitContactForm(type, payload) {
  await invokeEdgeJson(
    CONTACT_FORM,
    { type, ...payload },
    {
      auth: 'none',
      errorMessage: `${type === 'support' ? 'Support request' : 'Feedback submission'} failed`,
    }
  )
}
